/**
 * ActivityModal — sjednocená aktivita Codable projektu.
 *
 * Timeline z:
 *   • MCP project_log (Cursor / Claude / dashboard tool calls)
 *   • Git commits (codable.history)
 *
 * Plus „probíhající práce“: actor s last_seen < PRESENCE_MS z MCP logu.
 * Git řádky umí bezpečný revert do DEV (stejně jako HistoryModal).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Check,
  GitCommit,
  RotateCcw,
  Terminal,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { Badge, Button, EmptyState, Modal } from '../../../components/ui';

const PRESENCE_MS = 5 * 60_000;

type Filter = 'all' | 'mcp' | 'git';

interface ProjectLogCall {
  id: string;
  tool_name: string;
  raw_tool_name?: string | null;
  action?: string | null;
  actor_entity_id?: string | null;
  actor_key_preview?: string | null;
  args_summary?: unknown;
  result_summary?: unknown;
  ok: boolean;
  error?: string | null;
  duration_ms: number;
  created_at: string;
}

interface ProjectLogResp {
  ok: boolean;
  calls?: ProjectLogCall[];
  error?: string;
}

interface Commit {
  sha: string;
  short: string;
  title: string;
  author: string;
  created_at: string;
}

interface HistoryListResp {
  ok: boolean;
  commits?: Commit[];
  error?: string;
}

interface RevertResp {
  ok: boolean;
  restore_commit?: string;
  restored_to?: string;
  short?: string;
  nothing_to_commit?: boolean;
  message?: string;
  runnable?: boolean;
  preview?: { rebuilt?: boolean; mode?: string; visible_now?: string; ok?: boolean };
  error?: string;
}

type TimelineItem =
  | {
    kind: 'mcp';
    id: string;
    ts: number;
    call: ProjectLogCall;
  }
  | {
    kind: 'git';
    id: string;
    ts: number;
    commit: Commit;
    isHead: boolean;
  };

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function relTime(iso: string, tr: (k: string, opts?: Record<string, unknown>) => string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return tr('codable.historyJustNow');
  const m = Math.floor(s / 60);
  if (m < 60) return tr('codable.historyAgoMin', { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return tr('codable.historyAgoH', { n: h });
  const d = Math.floor(h / 24);
  if (d < 30) return tr('codable.historyAgoD', { n: d });
  return new Date(ts).toLocaleDateString();
}

function actorLabel(call: ProjectLogCall, unknown: string): string {
  const c = call as ProjectLogCall & {
    actor_display?: string | null;
    actor_entity_label?: string | null;
    actor_key_label?: string | null;
  };
  const who = c.actor_display ?? c.actor_entity_label ?? c.actor_entity_id ?? unknown;
  const token = c.actor_key_label
    ? (c.actor_key_preview ? `${c.actor_key_label} (${c.actor_key_preview}…)` : c.actor_key_label)
    : c.actor_key_preview
    ? `${c.actor_key_preview}…`
    : null;
  return token ? `${who} · ${token}` : who;
}

function shortJson(value: unknown): string {
  if (!value || typeof value !== 'object') return '';
  try {
    const s = JSON.stringify(value);
    return s.length > 160 ? `${s.slice(0, 160)}…` : s;
  } catch {
    return '';
  }
}

function friendlyTool(name: string): string {
  return name
    .replace(/^codable[._]/, '')
    .replace(/_/g, ' ');
}

export default function ActivityModal(
  { slug, onClose, onBack }: { slug: string; onClose: () => void; onBack?: () => void },
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');
  const [confirmSha, setConfirmSha] = useState<string | null>(null);
  // Inline výsledek obnovy — bez popup toastů by success zmizel v bell filtru.
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);

  const logQ = useQuery({
    queryKey: ['codable-project-log', slug],
    queryFn: () => api.mcp<ProjectLogResp>('codable.project_log', { slug, limit: 150 }),
  });

  const histQ = useQuery({
    queryKey: ['codable-history', slug],
    queryFn: () => api.mcp<HistoryListResp>('codable.history', { slug, action: 'list', limit: 40 }),
  });

  const revert = useMutation({
    mutationFn: (sha: string) =>
      api.mcp<RevertResp>('codable.history', { slug, action: 'restore', sha }),
    onSuccess: (r) => {
      setConfirmSha(null);
      qc.invalidateQueries({ queryKey: ['codable-history', slug] });
      qc.invalidateQueries({ queryKey: ['codable-project'] });
      qc.invalidateQueries({ queryKey: ['codable-tree', slug] });
      qc.invalidateQueries({ queryKey: ['codable-project-status', slug] });
      if (!r.ok) {
        const text = r.error ?? t('codable.historyRestoreFail');
        setFeedback({ ok: false, text });
        toast.error(text);
        return;
      }
      const shaShort = (r.restore_commit ?? r.short ?? r.restored_to ?? '').slice(0, 8);
      const previewNote = r.preview?.rebuilt
        ? t('codable.historyPreviewRebuilt', 'Preview přestavěn.')
        : r.preview?.mode === 'dev'
        ? t('codable.historyPreviewHmr', 'Preview HMR.')
        : (r.preview?.visible_now ?? '');
      const text = (r.message && r.message.trim()) || (r.nothing_to_commit
        ? t('codable.historyAlreadyAt', { sha: shaShort, preview: previewNote })
        : t('codable.historyRestored', { sha: shaShort, preview: previewNote }));
      const ok = r.runnable !== false;
      setFeedback({ ok, text });
      if (ok) toast.success(text);
      else toast.error(text);
      window.dispatchEvent(new CustomEvent('codable:preview-refresh', { detail: { slug } }));
    },
    onError: (e: unknown) => {
      setConfirmSha(null);
      const text = t('codable.errorGeneric', { msg: String((e as Error).message ?? e) });
      setFeedback({ ok: false, text });
      toast.error(text);
    },
  });

  const calls = logQ.data?.ok !== false ? (logQ.data?.calls ?? []) : [];
  const commits = histQ.data?.ok !== false ? (histQ.data?.commits ?? []) : [];

  const presence = useMemo(() => {
    const now = Date.now();
    const map = new Map<string, { label: string; last: number; count: number }>();
    for (const c of calls) {
      const ts = new Date(c.created_at).getTime();
      if (Number.isNaN(ts) || now - ts > PRESENCE_MS) continue;
      const label = actorLabel(c, t('codable.logUnknownCaller'));
      const key = c.actor_entity_id ?? c.actor_key_preview ?? label;
      const prev = map.get(key);
      if (!prev || ts > prev.last) {
        map.set(key, { label, last: ts, count: (prev?.count ?? 0) + 1 });
      } else {
        prev.count += 1;
      }
    }
    return [...map.values()].sort((a, b) => b.last - a.last);
  }, [calls, t]);

  const timeline = useMemo(() => {
    const items: TimelineItem[] = [];
    if (filter !== 'git') {
      for (const call of calls) {
        const ts = new Date(call.created_at).getTime();
        items.push({
          kind: 'mcp',
          id: `mcp-${call.id}`,
          ts: Number.isNaN(ts) ? 0 : ts,
          call,
        });
      }
    }
    if (filter !== 'mcp') {
      commits.forEach((commit, i) => {
        const ts = new Date(commit.created_at).getTime();
        items.push({
          kind: 'git',
          id: `git-${commit.sha}`,
          ts: Number.isNaN(ts) ? 0 : ts,
          commit,
          isHead: i === 0,
        });
      });
    }
    items.sort((a, b) => b.ts - a.ts);
    return items;
  }, [calls, commits, filter]);

  const loading = logQ.isLoading || histQ.isLoading;
  const logErr = logQ.data?.ok === false ? logQ.data.error : null;
  const histErr = histQ.data?.ok === false ? histQ.data.error : null;

  const filters: { id: Filter; label: string }[] = [
    { id: 'all', label: t('codable.activityFilterAll') },
    { id: 'mcp', label: t('codable.activityFilterMcp') },
    { id: 'git', label: t('codable.activityFilterGit') },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      onBack={onBack}
      backLabel={t('codable.wsSettings')}
      title={t('codable.activityTitle')}
      size='xl'
    >
      <div className='space-y-3'>
        {/* Presence — kdo v posledních minutách volal MCP */}
        <div className='rounded-xl border border-gray-800 bg-gray-900/55 px-3 py-2.5'>
          <div className='text-[10px] font-semibold uppercase tracking-widest text-gray-600 mb-1.5'>
            {t('codable.activityPresence')}
          </div>
          {presence.length === 0
            ? (
              <p className='text-xs text-gray-500'>{t('codable.activityPresenceEmpty')}</p>
            )
            : (
              <div className='flex flex-wrap gap-2'>
                {presence.map((p) => (
                  <div
                    key={`${p.label}-${p.last}`}
                    className='inline-flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1'
                  >
                    <span className='relative flex h-2 w-2'>
                      <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-40' />
                      <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-400' />
                    </span>
                    <span className='text-xs text-emerald-100 truncate max-w-[14rem]'>{p.label}</span>
                    <span className='text-[10px] text-emerald-300/70'>
                      {relTime(new Date(p.last).toISOString(), t)} · {p.count}×
                    </span>
                  </div>
                ))}
              </div>
            )}
        </div>

        {/* Filtry */}
        <div className='flex flex-wrap items-center gap-1.5'>
          {filters.map((f) => (
            <button
              key={f.id}
              type='button'
              onClick={() => setFilter(f.id)}
              className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                filter === f.id
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-500 hover:bg-gray-800/60 hover:text-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className='ml-auto text-[11px] text-gray-600'>
            {timeline.length} {t('codable.activityEvents')}
          </span>
        </div>

        {(logErr || histErr) && (
          <div className='space-y-1 text-xs text-amber-300'>
            {logErr && <div>{t('codable.logLoadError')}: {logErr}</div>}
            {histErr && <div>{t('codable.historyLoadError')}: {histErr}</div>}
          </div>
        )}

        {feedback && (
          <div
            role='status'
            className={`rounded-xl border px-3 py-2 text-sm ${
              feedback.ok
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                : 'border-red-500/40 bg-red-500/10 text-red-200'
            }`}
          >
            {feedback.text}
          </div>
        )}

        <div className='max-h-[58vh] space-y-1 overflow-y-auto -m-1 p-1'>
          {loading
            ? (
              <div className='space-y-2 py-2' aria-hidden='true'>
                {[...Array(7)].map((_, i) => (
                  <div key={i} className='h-12 rounded-xl bg-gray-800 animate-pulse' />
                ))}
              </div>
            )
            : timeline.length === 0
            ? (
              <EmptyState
                icon={Activity}
                title={t('codable.activityEmpty')}
                description={t('codable.activityEmptyDesc')}
              />
            )
            : timeline.map((item) => {
              if (item.kind === 'mcp') {
                const call = item.call;
                const args = shortJson(call.args_summary);
                const result = shortJson(call.result_summary);
                return (
                  <div
                    key={item.id}
                    className='rounded-xl border border-gray-800 bg-gray-900/35 px-3 py-2.5'
                  >
                    <div className='flex items-start gap-3'>
                      <div
                        className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                          call.ok
                            ? 'bg-sky-500/10 text-sky-300'
                            : 'bg-red-500/10 text-red-300'
                        }`}
                      >
                        {call.ok ? <Terminal size={14} /> : <X size={14} />}
                      </div>
                      <div className='min-w-0 flex-1'>
                        <div className='flex flex-wrap items-center gap-2 min-w-0'>
                          <Badge variant='blue'>{t('codable.activityKindMcp')}</Badge>
                          <span className='truncate text-xs font-medium text-white'>
                            {friendlyTool(call.tool_name)}
                          </span>
                          {call.action && <Badge variant='gray'>{call.action}</Badge>}
                          <Badge variant={call.ok ? 'green' : 'red'}>
                            {call.ok ? 'ok' : 'fail'}
                          </Badge>
                        </div>
                        <div className='mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500'>
                          <span>{formatTime(call.created_at)}</span>
                          <span>{actorLabel(call, t('codable.logUnknownCaller'))}</span>
                          <span>{call.duration_ms} ms</span>
                          <span className='font-mono text-gray-600 truncate max-w-[12rem]'>
                            {call.tool_name}
                          </span>
                        </div>
                        {call.error && (
                          <div className='mt-1 truncate text-xs text-red-300'>{call.error}</div>
                        )}
                        {(args || result) && (
                          <div className='mt-1 grid grid-cols-1 gap-1 text-[11px] font-mono text-gray-600 lg:grid-cols-2'>
                            {args && <div className='truncate'>args {args}</div>}
                            {result && <div className='truncate'>result {result}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              const c = item.commit;
              return (
                <div
                  key={item.id}
                  className='rounded-xl border border-gray-800 bg-gray-900/35 px-3 py-2.5'
                >
                  <div className='flex items-start gap-3'>
                    <div className='mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-500/10 text-violet-300'>
                      {item.isHead ? <Check size={14} /> : <GitCommit size={14} />}
                    </div>
                    <div className='min-w-0 flex-1'>
                      <div className='flex flex-wrap items-center gap-2 min-w-0'>
                        <Badge variant='purple'>{t('codable.activityKindGit')}</Badge>
                        <span className='font-mono text-[11px] text-gray-500'>{c.short}</span>
                        <span className='truncate text-sm text-white'>{c.title}</span>
                        {item.isHead && (
                          <Badge variant='blue'>{t('codable.historyCurrent')}</Badge>
                        )}
                      </div>
                      <div className='mt-1 text-[11px] text-gray-500'>
                        {c.author} · {relTime(c.created_at, t)} · {formatTime(c.created_at)}
                      </div>
                    </div>
                    {!item.isHead && (
                      <Button
                        size='sm'
                        variant={confirmSha === c.sha ? 'danger' : 'secondary'}
                        icon={<RotateCcw size={13} />}
                        loading={revert.isPending && revert.variables === c.sha}
                        disabled={revert.isPending}
                        onClick={() => {
                          if (confirmSha === c.sha) revert.mutate(c.sha);
                          else setConfirmSha(c.sha);
                        }}
                        title={confirmSha === c.sha
                          ? t('codable.historyConfirmTitle')
                          : t('codable.historyRestoreHint')}
                        className='shrink-0'
                      >
                        {confirmSha === c.sha
                          ? t('codable.historyConfirmDev')
                          : t('codable.historyRestoreDev')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </Modal>
  );
}
